import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { Grid } from '~/engine/data/common/Grid';
import { computeGridAABB } from '~/domain/services/imageService';
import { formatMmAsCm } from '~/utils/units';

/** Int16 output: two bytes per voxel. */
const BYTES_PER_VOXEL = 2;

interface OutputGeometryProps {
    /** The grid the reformat will produce. */
    grid: Grid;
}

/**
 * What the reformat will write, read off the destination grid itself
 * rather than estimated alongside it — so the figures here are the ones
 * the output carries, including any rounding the grid had to do.
 *
 * What the output will contain that was not acquired is stated by
 * {@link ReformatWarnings}, beside the control that starts the reformat,
 * because this block scrolls and that statement must not.
 */
export default function OutputGeometry({ grid }: OutputGeometryProps) {
    const intl = useIntl();
    const t = (id: string) => intl.formatMessage({ id });

    const { worldMin, worldMax } = computeGridAABB(grid);
    const megabytes =
        (grid.sizeX * grid.sizeY * grid.sizeZ * BYTES_PER_VOXEL) /
        (1024 * 1024);

    const rows: [string, string][] = [
        [t('reformatMatrix'), `${grid.sizeX} × ${grid.sizeY} × ${grid.sizeZ}`],
        [
            t('reformatVoxelSize'),
            `${formatMmAsCm(grid.resX, 3)} × ${formatMmAsCm(grid.resY, 3)} × ${formatMmAsCm(grid.resZ, 3)} cm`,
        ],
        [
            t('fieldOfView'),
            `${formatMmAsCm(worldMax[0] - worldMin[0], 1)} × ${formatMmAsCm(worldMax[1] - worldMin[1], 1)} × ${formatMmAsCm(worldMax[2] - worldMin[2], 1)} cm`,
        ],
        [
            t('reformatOutputSliceRange'),
            // Slice centres, matching what the range control shows.
            `${formatMmAsCm(worldMin[2] + grid.resZ / 2)} – ${formatMmAsCm(worldMax[2] - grid.resZ / 2)} cm`,
        ],
        [
            // The region can be moved off the source's centre on the scout,
            // and a position that can only be seen cannot be checked or
            // reproduced.
            t('reformatCentre'),
            `${formatMmAsCm((worldMin[0] + worldMax[0]) / 2, 1)}, ${formatMmAsCm((worldMin[1] + worldMax[1]) / 2, 1)} cm`,
        ],
        [t('reformatDataSize'), `~${megabytes.toFixed(1)} MB`],
    ];

    return (
        <Box>
            <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
            >
                {t('reformatOutputGeometry')}
            </Typography>
            <Box
                display="grid"
                gridTemplateColumns="auto 1fr"
                columnGap={1}
                sx={{
                    fontSize: '0.75rem',
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {rows.map(([label, value]) => (
                    <Box key={label} display="contents">
                        <Typography variant="caption" color="text.secondary">
                            {label}
                        </Typography>
                        <Typography variant="caption">{value}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
